import { ImagePlus, Save } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { DestinyBadge } from "@/components/karma/DestinyBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { SupabaseAuthCard, SupabaseConnectionCard } from "@/components/supabase";

import { getDigitShortLabel } from "@/lib/karma/insights";
import { computeArchetypeProfile } from "@/lib/karma/archetypes";

import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { computeProfileCompletion } from "@/lib/karma/profile";
import { computeCoachTodaySummary } from "@/lib/karma/coach";
import { getLocalDateKey } from "@/lib/karma/missions";
import type { KarmaCoachGoal } from "@/lib/karma/types";
import { addPhoto, ensureStore, getMe, setCoachGoal, updateUser } from "@/lib/karma/storage";

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Dosya okunamadı"));
    reader.readAsDataURL(file);
  });
}

export default function KarmaMe() {
  const [storeVersion, setStoreVersion] = useState(0);
  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);

  const me = getMe(store);
  const numerology = useMemo(
    () => computeKarmaNumerology(me.displayName, me.birthDate),
    [me.displayName, me.birthDate],
  );

  const archetypeProfile = useMemo(() => computeArchetypeProfile(numerology), [numerology]);

  const completion = useMemo(() => computeProfileCompletion(me), [me]);

  const [displayName, setDisplayName] = useState(me.displayName);
  const [bio, setBio] = useState(me.bio);
  const [city, setCity] = useState(me.city ?? "");
  const [intent, setIntent] = useState(me.intent ?? "belirsiz");
  const [showBirthDayMonth, setShowBirthDayMonth] = useState(Boolean(me.showBirthDayMonth));

  const initialCoachGoal: KarmaCoachGoal =
    me.coach?.goal ?? (me.intent && me.intent !== "belirsiz" ? "iliski" : "denge");
  const [coachGoal, setCoachGoalState] = useState<KarmaCoachGoal>(initialCoachGoal);

  const [prefMinAge, setPrefMinAge] = useState(String(me.prefMinAge ?? 18));
  const [prefMaxAge, setPrefMaxAge] = useState(String(me.prefMaxAge ?? 99));
  const [prefOnlyMatchEnabled, setPrefOnlyMatchEnabled] = useState(Boolean(me.prefOnlyMatchEnabled));

  const [prefDestinyNumbers, setPrefDestinyNumbers] = useState<number[]>(me.prefDestinyNumbers ?? []);
  const [prefHeartDesireNumbers, setPrefHeartDesireNumbers] = useState<number[]>(
    me.prefHeartDesireNumbers ?? [],
  );
  const [prefExpressionNumbers, setPrefExpressionNumbers] = useState<number[]>(
    me.prefExpressionNumbers ?? [],
  );
  const [prefKarmicDebtNumbers, setPrefKarmicDebtNumbers] = useState<number[]>(
    me.prefKarmicDebtNumbers ?? [],
  );
  const [prefOnlySimilarStatus, setPrefOnlySimilarStatus] = useState(Boolean(me.prefOnlySimilarStatus));

  const [day, setDay] = useState(String(me.birthDate.day));
  const [month, setMonth] = useState(String(me.birthDate.month));
  const [year, setYear] = useState(String(me.birthDate.year));

  const fileRef = useRef<HTMLInputElement | null>(null);

  const save = () => {
    const birthDate = parseBirthDate({
      day: Number(day),
      month: Number(month),
      year: Number(year),
    });

    const minAge = Math.max(18, Math.min(99, Number(prefMinAge) || 18));
    const maxAge = Math.max(minAge, Math.min(99, Number(prefMaxAge) || 99));

    const normalizeDigits = (arr: number[]) => {
      const unique = Array.from(
        new Set(arr.map((n) => Math.trunc(Number(n))).filter((n) => Number.isFinite(n) && n >= 1 && n <= 9)),
      );
      return unique.length ? unique : undefined;
    };

    updateUser({
      ...me,
      displayName: displayName.trim() || me.displayName,
      bio: bio,
      city: city.trim() || undefined,
      intent: intent === "belirsiz" ? undefined : (intent as any),
      showBirthDayMonth,
      prefMinAge: minAge,
      prefMaxAge: maxAge,
      prefOnlyMatchEnabled,
      prefDestinyNumbers: normalizeDigits(prefDestinyNumbers),
      prefHeartDesireNumbers: normalizeDigits(prefHeartDesireNumbers),
      prefExpressionNumbers: normalizeDigits(prefExpressionNumbers),
      prefKarmicDebtNumbers: normalizeDigits(prefKarmicDebtNumbers),
      prefOnlySimilarStatus,
      birthDate,
    });

    setStoreVersion((v) => v + 1);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Panel</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Profilini düzenle, eşleşmeyi aç/kapat, fotoğraf ekle.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/">Keşif</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/karma/cember">Çember</Link>
          </Button>
          <Button asChild className="rounded-full">
            <Link to={`/karma/u/${me.handle}`}>Profil</Link>
          </Button>
        </div>
      </div>

      <Card className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">Numeroloji</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Satış sayfası ve yönetim paneli bağlantıları.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/numeroloji">Landing</Link>
            </Button>
            <Button asChild className="rounded-full">
              <Link to="/admin/numeroloji">Yönetim</Link>
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">Profil tamamlanma</div>
            <div className="mt-1 text-sm text-muted-foreground">
              %{completion.percent} — {completion.missing.length ? "Eksikler: " + completion.missing.join(", ") : "Hazır"}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={completion.percent} />
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <DestinyBadge destiny={numerology.destinyNumber} label="Kader Sayın" />
            <div className="text-sm text-muted-foreground">PIN: {numerology.pinText}</div>
            <div className="text-sm text-muted-foreground">
              Arketip: {archetypeProfile.archetype.name} ({archetypeProfile.elementLabel})
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border bg-muted/30 px-4 py-3">
            <div className="grid gap-1">
              <div className="text-sm font-medium">Eşleşme modu</div>
              <div className="text-xs text-muted-foreground">
                Kapalıyken Keşif ekranı spritüel koç görevlerine döner.
              </div>
            </div>
            <Switch
              checked={me.matchEnabled}
              onCheckedChange={(checked) => {
                updateUser({ ...me, matchEnabled: checked });
                setStoreVersion((v) => v + 1);
              }}
            />
          </div>
        </div>
      </Card>

      <SupabaseAuthCard me={me} />
      <SupabaseConnectionCard />

      <Card className="p-5">
        <div className="font-display text-lg font-semibold tracking-tight">Koç Modu</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Koç modunda görevler <span className="font-medium">senin odak alanına</span> göre kişiselleşir.
        </div>
        <Separator className="my-4" />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label>Odak alanı</Label>
            <Select
              value={coachGoal}
              onValueChange={(v) => {
                const next = v as KarmaCoachGoal;
                setCoachGoalState(next);
                setCoachGoal(next);
                setStoreVersion((x) => x + 1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seç" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="denge">Denge</SelectItem>
                <SelectItem value="iliski">İlişki</SelectItem>
                <SelectItem value="para">Para</SelectItem>
                <SelectItem value="saglik">Sağlık</SelectItem>
                <SelectItem value="uretim">Üretim</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(() => {
            const stats = computeCoachTodaySummary(me, new Date());
            const todayKey = getLocalDateKey(new Date());
            const coach = me.coach;

            const isComplete = (dateKey: string) => {
              const ids = coach?.completedByDate?.[dateKey] ?? [];
              return ids.includes("core-starter") && ids.includes("core-main") && ids.includes("core-closing");
            };

            const last7 = Array.from({ length: 7 }).map((_, idx) => {
              const d = new Date();
              d.setDate(d.getDate() - idx);
              const key = getLocalDateKey(d);
              return {
                key,
                label: d.toLocaleDateString("tr-TR", { weekday: "short" }),
                complete: isComplete(key),
              };
            });

            const todayDone = isComplete(todayKey);

            return (
              <div className="rounded-2xl border bg-muted/30 p-4 sm:col-span-2">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold">Seri & seviye</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      Seri: {stats.streak} gün • Seviye: {stats.level.level} • Bugün: {stats.todayXp} XP
                      {todayDone ? "" : " • Bugün eksik"}
                    </div>
                    {stats.titles.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {stats.titles.slice(0, 2).map((t) => (
                          <span
                            key={t.id}
                            className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                            title={t.description}
                          >
                            {t.name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/">Koç ekranı</Link>
                  </Button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {last7.map((d) => (
                    <span
                      key={d.key}
                      className={
                        d.complete
                          ? "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                          : "rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
                      }
                      title={d.key}
                    >
                      {d.label}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </Card>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="p-5">
            <div className="font-display text-lg font-semibold tracking-tight">Profil bilgileri</div>
            <Separator className="my-4" />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="displayName">İsim</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="city">Şehir</Label>
                <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>

              <div className="grid gap-2">
                <Label>Niyet</Label>
                <Select value={intent} onValueChange={(v) => setIntent(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seç" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="belirsiz">Belirtmek istemiyorum</SelectItem>
                    <SelectItem value="tanisma">Tanışma</SelectItem>
                    <SelectItem value="arkadaslik">Arkadaşlık</SelectItem>
                    <SelectItem value="ciddi">Ciddi ilişki</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="day">Gün</Label>
                <Input id="day" value={day} onChange={(e) => setDay(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="month">Ay</Label>
                <Input
                  id="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                />
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="year">Yıl</Label>
                <Input
                  id="year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>

              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  rows={4}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-between gap-3 rounded-xl border bg-muted/30 px-4 py-3 sm:col-span-2">
                <div>
                  <div className="text-sm font-medium">Doğum gün/ayını göster</div>
                  <div className="text-xs text-muted-foreground">Kapalıyken sadece yaş görünür.</div>
                </div>
                <Switch checked={showBirthDayMonth} onCheckedChange={setShowBirthDayMonth} />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Button onClick={save} className="rounded-full">
                <Save className="mr-2 h-4 w-4" /> Kaydet
              </Button>
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlus className="mr-2 h-4 w-4" /> Fotoğraf ekle
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const url = await readFileAsDataUrl(file);
                  addPhoto(me.id, url);
                  setStoreVersion((v) => v + 1);
                  e.target.value = "";
                }}
              />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <div className="font-display text-lg font-semibold tracking-tight">Fotoğraflar</div>
            <Separator className="my-4" />

            {me.photos.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Henüz fotoğraf yok. "Fotoğraf ekle" ile ekleyebilirsin.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {me.photos.slice(0, 6).map((p) => (
                  <div
                    key={p.id}
                    className="aspect-square overflow-hidden rounded-2xl border bg-muted"
                  >
                    <img
                      src={p.url}
                      alt={me.displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </section>

      <Card className="p-5">
        <div className="font-display text-lg font-semibold tracking-tight">Eşleşme tercihleri</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Keşif ekranında hangi profillerin gösterileceğini belirler.
        </div>
        <Separator className="my-4" />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="prefMinAge">Minimum yaş</Label>
            <Input
              id="prefMinAge"
              value={prefMinAge}
              onChange={(e) => setPrefMinAge(e.target.value)}
              inputMode="numeric"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="prefMaxAge">Maksimum yaş</Label>
            <Input
              id="prefMaxAge"
              value={prefMaxAge}
              onChange={(e) => setPrefMaxAge(e.target.value)}
              inputMode="numeric"
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border bg-muted/30 px-4 py-3 sm:col-span-2">
            <div>
              <div className="text-sm font-medium">Sadece eşleşmeye açık profiller</div>
              <div className="text-xs text-muted-foreground">Kapalı profiller keşifte görünmez.</div>
            </div>
            <Switch checked={prefOnlyMatchEnabled} onCheckedChange={setPrefOnlyMatchEnabled} />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border bg-muted/30 px-4 py-3 sm:col-span-2">
            <div>
              <div className="text-sm font-medium">Statü filtresi</div>
              <div className="text-xs text-muted-foreground">
                Açıkken statünle benzer seviyedeki profilleri gösterir. Kapalıyken herkesi görürsün.
              </div>
            </div>
            <Switch checked={prefOnlySimilarStatus} onCheckedChange={setPrefOnlySimilarStatus} />
          </div>
        </div>

        <Separator className="my-6" />

        {(() => {
          const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

          const toValue = (arr: number[]) => arr.map(String);
          const fromValue = (arr: string[]) =>
            Array.from(
              new Set(
                (arr ?? [])
                  .map((v) => Math.trunc(Number(v)))
                  .filter((n) => Number.isFinite(n) && n >= 1 && n <= 9),
              ),
            );

          const Section = (props: {
            title: string;
            description: string;
            value: number[];
            onChange: (next: number[]) => void;
          }) => {
            const selected = props.value;
            return (
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold">{props.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{props.description}</div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-full px-3"
                    onClick={() => props.onChange([])}
                    disabled={selected.length === 0}
                  >
                    Temizle
                  </Button>
                </div>

                <div className="mt-3">
                  <ToggleGroup
                    type="multiple"
                    value={toValue(selected)}
                    onValueChange={(v) => props.onChange(fromValue(v))}
                    className="flex flex-wrap justify-start gap-2"
                  >
                    {DIGITS.map((d) => (
                      <ToggleGroupItem
                        key={d}
                        value={String(d)}
                        aria-label={`${props.title} ${d}`}
                        className="h-auto rounded-xl px-3 py-2"
                      >
                        <div className="flex flex-col items-center">
                          <div className="text-sm font-semibold">{d}</div>
                          <div className="text-[11px] text-muted-foreground">{getDigitShortLabel(d)}</div>
                        </div>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>

                  <div className="mt-3 text-xs text-muted-foreground">
                    {selected.length ? `Seçili: ${selected.join(", ")}` : "Seçmezsen filtre uygulanmaz."}
                  </div>
                </div>
              </div>
            );
          };

          return (
            <div className="grid gap-4 lg:grid-cols-2">
              <Section
                title="Kader sayısı"
                description="Örn: 5 → özgür ruhlu. Seçtiğin kader sayılarıyla eşleşen profilleri gösterir."
                value={prefDestinyNumbers}
                onChange={setPrefDestinyNumbers}
              />
              <Section
                title="Kalp arzusu"
                description="Duygusal ihtiyaç dili. Uyumlu hissettiklerini seçebilirsin."
                value={prefHeartDesireNumbers}
                onChange={setPrefHeartDesireNumbers}
              />
              <Section
                title="İfade"
                description="İletişim/üretim tarzı. Birlikte iş/üretim için faydalı."
                value={prefExpressionNumbers}
                onChange={setPrefExpressionNumbers}
              />
              <Section
                title="Karmik borç"
                description="Seçtiğin karmik borçlardan en az birine sahip profilleri gösterir."
                value={prefKarmicDebtNumbers}
                onChange={setPrefKarmicDebtNumbers}
              />
            </div>
          );
        })()}
      </Card>

      <Card className="p-5">
        <div className="font-display text-lg font-semibold tracking-tight">Karmik kodlar</div>
        <div className="mt-1 text-sm text-muted-foreground">
          {numerology.karmicCodes.length ? numerology.karmicCodes.join(", ") : "Dengeli"}
        </div>
      </Card>
    </div>
  );
}
